import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Users, 
  School, 
  FileText, 
  MessageSquare, 
  TrendingUp, 
  Calendar,
  DollarSign,
  UserCheck,
  Clock
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import { api } from '../services/api'

interface DashboardStats {
  totalUsers: number
  totalSchools: number
  totalBookings: number
  totalTestimonials: number
  totalConsultations: number
  pendingBookings: number
  verifiedSchools: number
  activeUsers: number
  monthlyRevenue: number
  userGrowth: number
  bookingGrowth: number
  schoolGrowth: number
}

const Dashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalSchools: 0,
    totalBookings: 0,
    totalTestimonials: 0,
    totalConsultations: 0,
    pendingBookings: 0,
    verifiedSchools: 0,
    activeUsers: 0,
    monthlyRevenue: 0,
    userGrowth: 0,
    bookingGrowth: 0,
    schoolGrowth: 0
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Fetch real data from APIs
      const [usersResponse, schoolsResponse, bookingsResponse] = await Promise.all([
        api.get('/users/stats'),
        api.get('/schools/stats'),
        api.get('/bookings/admin/stats')
      ])

      const usersStats = usersResponse.data.data.stats
      const schoolsStats = schoolsResponse.data.data.stats  
      const bookingsStats = bookingsResponse.data.data.stats

      setStats({
        totalUsers: usersStats.totalUsers || 0,
        totalSchools: schoolsStats.totalSchools || 0,
        totalBookings: bookingsStats.totalBookings || 0,
        totalTestimonials: 0, // TODO: Add testimonials stats API
        totalConsultations: 0, // TODO: Add consultations stats API
        pendingBookings: bookingsStats.yPendingBookings || 0,
        verifiedSchools: schoolsStats.activeSchools || 0,
        activeUsers: usersStats.activeUsers || 0,
        monthlyRevenue: 0, // TODO: Add revenue calculation
        userGrowth: 0, // TODO: Add growth calculation
        bookingGrowth: 0, // TODO: Add growth calculation
        schoolGrowth: 0 // TODO: Add growth calculation
      })
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
      // Fallback to mock data if API fails
      setStats({
        totalUsers: 0,
        totalSchools: 0,
        totalBookings: 0,
        totalTestimonials: 0,
        totalConsultations: 0,
        pendingBookings: 0,
        verifiedSchools: 0,
        activeUsers: 0,
        monthlyRevenue: 0,
        userGrowth: 0,
        bookingGrowth: 0,
        schoolGrowth: 0
      })
    } finally {
      setLoading(false)
    }
  }

  const userGrowthData = [
    { name: 'Jan', users: 400, bookings: 240 },
    { name: 'Feb', users: 300, bookings: 139 },
    { name: 'Mar', users: 200, bookings: 980 },
    { name: 'Apr', users: 278, bookings: 390 },
    { name: 'May', users: 189, bookings: 480 },
    { name: 'Jun', users: 239, bookings: 380 },
    { name: 'Jul', users: 349, bookings: 430 },
  ]

  const schoolStatusData = [
    { name: 'Verified', value: 67, color: '#10b981' },
    { name: 'Pending', value: 15, color: '#f59e0b' },
    { name: 'Rejected', value: 7, color: '#ef4444' },
  ]

  const bookingStatusData = [
    { name: 'Approved', value: stats?.totalBookings ? Math.floor(stats.totalBookings * 0.6) : 0, color: '#10b981' },
    { name: 'Rejected', value: stats?.totalBookings ? Math.floor(stats.totalBookings * 0.1) : 0, color: '#ef4444' },
    { name: 'Completed', value: stats?.totalBookings ? Math.floor(stats.totalBookings * 0.25) : 0, color: '#3b82f6' },
    { name: 'Cancelled', value: stats?.totalBookings ? Math.floor(stats.totalBookings * 0.05) : 0, color: '#6b7280' },
  ]

  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers.toLocaleString(),
      change: `+${stats.userGrowth}%`,
      changeType: 'positive' as const,
      icon: Users,
      color: 'bg-blue-500'
    },
    {
      title: 'Yoga Schools',
      value: stats.totalSchools.toLocaleString(),
      change: `+${stats.schoolGrowth}%`,
      changeType: 'positive' as const,
      icon: School,
      color: 'bg-green-500'
    },
    {
      title: 'Bookings',
      value: stats.totalBookings.toLocaleString(),
      change: `+${stats.bookingGrowth}%`,
      changeType: 'positive' as const,
      icon: FileText,
      color: 'bg-purple-500'
    },
    {
      title: 'Monthly Revenue',
      value: `$${stats.monthlyRevenue.toLocaleString()}`,
      change: '+8.2%',
      changeType: 'positive' as const,
      icon: DollarSign,
      color: 'bg-yellow-500'
    },
    {
      title: 'Active Users',
      value: stats.activeUsers.toLocaleString(),
      change: '+5.1%',
      changeType: 'positive' as const,
      icon: UserCheck,
      color: 'bg-indigo-500'
    },
    {
      title: 'Pending Bookings',
      value: stats.pendingBookings.toLocaleString(),
      change: '-12.3%',
      changeType: 'negative' as const,
      icon: Clock,
      color: 'bg-orange-500'
    }
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-full overflow-hidden">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Dashboard</h1>
            <p className="text-gray-600">Welcome back! Here's what's happening with your platform.</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Last updated</p>
            <p className="text-sm font-medium text-gray-700">{new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-600 mb-2">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-900">{card.value}</p>
                </div>
                <div className={`p-4 rounded-2xl ${card.color} shadow-lg flex-shrink-0`}>
                  <card.icon className="h-8 w-8 text-white" />
                </div>
              </div>
              <div className="mt-6 flex items-center">
                <TrendingUp className={`h-4 w-4 ${
                  card.changeType === 'positive' ? 'text-emerald-500' : 'text-red-500'
                }`} />
                <span className={`text-sm font-semibold ml-2 ${
                  card.changeType === 'positive' ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {card.change}
                </span>
                <span className="text-sm text-gray-500 ml-2">from last month</span>
              </div>
            </motion.div>
        ))}
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Growth Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
          >
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">User & Application Growth</h3>
            <p className="text-gray-600">Monthly trends over the last 7 months</p>
          </div>
          <div>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="users" stroke="#3b82f6" strokeWidth={2} />
                <Line type="monotone" dataKey="bookings" stroke="#10b981" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* School Status Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
        >
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">School Verification Status</h3>
            <p className="text-gray-600">Current status of yoga school verifications</p>
          </div>
          <div>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={schoolStatusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {schoolStatusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Application Status Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="card"
      >
        <div className="card-header">
          <h3 className="text-lg font-semibold text-ivory-900">Application Status Distribution</h3>
          <p className="text-sm text-ivory-600">Breakdown of application statuses</p>
        </div>
        <div className="card-content">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={bookingStatusData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200"
      >
        <div className="mb-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Recent Activity</h3>
          <p className="text-gray-600">Latest platform activities</p>
        </div>
        <div>
          <div className="space-y-4">
            {[
              { action: 'New school registration', user: 'Yoga Bliss Rishikesh', time: '2 minutes ago', type: 'school' },
              { action: 'Application submitted', user: 'Sarah Johnson', time: '15 minutes ago', type: 'application' },
              { action: 'Testimonial added', user: 'Michael Chen', time: '1 hour ago', type: 'testimonial' },
              { action: 'School verified', user: 'Himalayan Yoga Academy', time: '2 hours ago', type: 'verification' },
              { action: 'Consultation scheduled', user: 'Emma Wilson', time: '3 hours ago', type: 'consultation' },
            ].map((activity, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                <div className={`p-2 rounded-full ${
                  activity.type === 'school' ? 'bg-green-100' :
                  activity.type === 'application' ? 'bg-blue-100' :
                  activity.type === 'testimonial' ? 'bg-purple-100' :
                  activity.type === 'verification' ? 'bg-yellow-100' :
                  'bg-indigo-100'
                }`}>
                  {activity.type === 'school' && <School className="h-4 w-4 text-green-600" />}
                  {activity.type === 'application' && <FileText className="h-4 w-4 text-blue-600" />}
                  {activity.type === 'testimonial' && <MessageSquare className="h-4 w-4 text-purple-600" />}
                  {activity.type === 'verification' && <UserCheck className="h-4 w-4 text-yellow-600" />}
                  {activity.type === 'consultation' && <Calendar className="h-4 w-4 text-indigo-600" />}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                  <p className="text-sm text-gray-600">{activity.user}</p>
                </div>
                <span className="text-xs text-gray-500">{activity.time}</span>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default Dashboard


